/*
 * @Author: yanghailong
 * @Date: 2023-08-23 09:06:16
 * @Last Modified by: yanghailong
 * @Last Modified time: 2025-08-01 15:37:00
 */
import Konva from 'konva'
import { BusElement, EBlockElement, ELinkElement, PCCElement } from './element/index.js'
import { DirectionX, DirectionY } from './ref-line.js'
import { Configs } from './data.js'
import { getImgSrcByName } from "@/topo/core/utils/img-load.js"

/**
 * @typedef {import('./graph-element').GraphElement} GraphElement 图元件
 */

/**
 * @typedef Vertex 顶点
 * @property {GraphElement} node
 * @property {import('konva').default.Circle} port
 */

/**
 * @typedef Edge
 * @property {string} uuid
 * @property {Vertex} source
 * @property {Vertex} target
 */

export class Painter {

  /**
   * @param {import('./topology').Topology} topology
   */
  constructor(topology) {
    this.topology = topology
    this.storage = topology.storage

    this.animations = []
  }

  paint() {
    const { storage } = this
    const { graph, stage, layerLines } = storage

    this.animations.forEach(anim => {
      anim.stop()
    })
    this.animations = []

    console.log('paint line')

    layerLines.removeChildren()


    // 绘制边
    graph.edges.forEach(edge => {
      let points = this._getLinePoints(edge.source, edge.target)
      let color = Configs.edgeColor;
      if (edge.source && edge.source.node instanceof BusElement) {
        // 如果 source.node 存在且有 fill 方法或 fill 属性，使用其填充色作为连线颜色
        if (edge.source.node.keyShape && edge.source.node.keyShape.fill) {
          color = edge.source.node.keyShape.fill()
        }
      }

      let line = new Konva.Line({
        points: points,
        stroke: color,
        id: edge.uuid,
        strokeWidth: 2,

      })


      // let arrow = new Konva.Arrow({
      //   points: [0, 0, 0, 20],
      //   pointerLength: 6,
      //   pointerWidth: 8,
      //   fill: '#0f0',
      //   stroke: '#0f0',
      //   // opacity: 0.5,
      //   name: 'arrow',
      //   // draggable: true,
      //   // visible: false,
      //   strokeWidth: 4,
      //   id: 'anim-arrow-' + edge.uuid
      // })

      Konva.Image.fromURL(getImgSrcByName('flyLine'), (flyLine) => {
        let [startX, startY, endX, endY] = points

        let dy = endY - startY
        let dx = endX - startX
        // alpha 为连线相对下方竖直方向的偏角（度）；流线素材默认竖直向下，故旋转量为 -alpha
        let alpha = Math.atan2(dx, dy) * 180 / Math.PI

        // 以图片水平中心为基准点，使流线居中于连线并绕自身中心旋转
        flyLine.offsetX(flyLine.width() / 2)
        flyLine.rotation(-alpha)
        flyLine.setAttrs({
          x: startX,
          y: startY,
          id: 'anim-arrow-' + edge.uuid,
        })

        const { isCancel, points: newPoints } = this._getIsReverseAndPoints(edge)
        if (!isCancel) {
          this.createAnim(flyLine, newPoints)
          layerLines.add(flyLine)
        }
      })

      // anim.id = 'line-anim-' + edge.uuid
      layerLines.add(line)

      // this.createAnim(arrow, points)
      // // 将圆形添加到图层
      // layerLines.add(arrow)
    })

    // 同步母线及其子节点相关连线的颜色
    this._syncBusChildLineColors()

    storage.layer.batchDraw()
  }

  /**
   * 遍历每个 BusElement 的子节点，并将这些子节点相关的连线颜色设置为该 BusElement 的颜色。
   * 如果一个 BusElement 有父 BusElement，则仍然使用自身的颜色（不会继承父级）。
   */
  _syncBusChildLineColors() {
    const { storage } = this
    const { layerLines } = storage
    const graph = storage.graph
    // 获取所有母线元素
    const buses = storage.displayElement.filter(ge => ge instanceof BusElement)

    buses.forEach(bus => {
      // 读取母线颜色（优先支持 Konva 的 getter 或直接属性）
      const ks = bus.keyShape
      const busColor = (typeof ks?.fill === 'function') ? ks.fill() : ks?.fill

      // 使用 DFS 遍历 adjList 收集该母线的子节点（不穿透子母线）
      const descendants = new Set()
      const visited = new Set()
      const stack = []
      visited.add(bus)
      const startNeighbors = graph.adjList.get(bus) || []
      startNeighbors.forEach(v => stack.push(v.node))

      while (stack.length) {
        const node = stack.pop()
        if (!node || visited.has(node)) continue
        visited.add(node)

        // 如果遇到另一个 BusElement，则不要继续穿透它，也不把它列为当前母线需要上色的子节点
        if (node instanceof BusElement) {
          continue
        }

        descendants.add(node)

        // 将 node 的直接子节点加入栈，继续遍历
        const neigh = graph.adjList.get(node) || []
        neigh.forEach(v => {
          if (!visited.has(v.node)) stack.push(v.node)
        })
      }

      // 遍历所有边并为相关边设置颜色：
      // - 边的 source 为母线
      // - 或者边的 source/target 为母线的子节点（让子节点的连线也显示为母线色）
      graph.edges.forEach(edge => {
        try {
          const sNode = edge.source.node
          const tNode = edge.target.node
          const isRelated = sNode === bus || descendants.has(sNode) || descendants.has(tNode)
          if (isRelated) {
            const line = layerLines.findOne('#' + edge.uuid)
            if (line && busColor) line.stroke(busColor)
          }
        } catch (e) {
          // 忽略单条边的错误，继续处理其它边
        }
      })
    })
  }

  /**
   * 添加动画
   * @param {import('konva').default.Image} arrow 箭头
   * @param {number[]} points 流线（起点和终点）
   */
  createAnim(arrow, points) {
    const { storage } = this
    const { graph, stage, layerLines } = storage
    let [startX, startY, endX, endY] = points

    // 以图片水平中心为基准（绕自身中心旋转/定位），无需再手动偏移半个宽度
    arrow.offsetX(arrow.width() / 2)

    let dy = endY - startY
    let dx = endX - startX
    let dist = Math.sqrt(dx * dx + dy * dy)
    let alpha = Math.atan2(dx, dy) * 180 / Math.PI

    arrow.rotation(-alpha)

    // 结束位置要考虑流动线条自身的尺寸
    const R = dist - arrow.height()
    let x1 = startX + R / dist * dx
    let y1 = startY + R / dist * dy

    // 动画时长为2秒
    const duration = 2000
    // 创建动画
    let anim = new Konva.Animation(function (frame) {
      var time = frame.time % duration;
      var progress = time / duration;


      if (progress > 1) progress = 1;

      // 更新终点位置
      var newX = startX + (x1 - startX) * progress;
      var newY = startY + (y1 - startY) * progress;

      arrow.x(newX)
      arrow.y(newY)

    }, layerLines);

    // 启动动画
    anim.start();
    this.animations.push(anim)
  }


  // 根据元素的位置信息 更新连线和端口
  update() {
    // log('update line')
    const { storage } = this
    const { graph, layerLines } = storage

    // 停止动画
    this.animations.forEach(anim => {
      anim.stop()
    })
    this.animations = []


    // 更新连线位置
    graph.edges.forEach(edge => {
      let points = this._getLinePoints(edge.source, edge.target)
      let line = storage.layerLines.findOne('#' + edge.uuid)
      line.points(points)

      // 更新箭头
      let arrow = storage.layerLines.findOne('#' + 'anim-arrow-' + edge.uuid)

      const { isCancel, points: newPoints } = this._getIsReverseAndPoints(edge)
      if (!isCancel) {
        this.createAnim(arrow, newPoints)
      }
    })

    // 同步母线及其子节点的连线颜色（确保 update 时也能生效）
    this._syncBusChildLineColors()

  }


  /**
   * 获取是否反向以及反向后的点
   * @param {Edge} edge 边
   * @param {string[]} keys 需要判断的属性
   * @returns {{ isReverse: boolean, isCancel: boolean, points: number[] }} 是否反向以及反向后的点
   */
  _getIsReverseAndPoints(edge, keys = ["功率"]) {
    let points = this._getLinePoints(edge.source, edge.target)
    const [startX, startY, endX, endY] = points
    let isReverse = false
    let isCancel = false

    // ELinkElement EBlockElement 为负充正放
    const ctors = [ELinkElement, EBlockElement]
    if (ctors.some(ctor => edge.target.node instanceof ctor)) {
      for (const key of keys) {
        const value = edge.target.node.getValue(key)
        if (value > 0) {
          isReverse = true
          break
        }
        if (value == 0 || value == null || value == '--') {
          isCancel = true
          break
        }
      }
    }

    if (edge.source.node instanceof PCCElement) {
      for (const key of keys) {
        const value = edge.source.node.getValue(key)
        // 如果是并网电表（meterType == 3）负充正放
        if (value > 0 && edge.source.node.meterType == 3) {
          isReverse = true
          break
        }
        if (value == 0 || value == null || value == '--') {
          isCancel = true
          break
        }
      }
    }

    if (edge.target.node instanceof PCCElement) {
      for (const key of keys) {
        const value = edge.target.node.getValue(key)
        // 如果是并网电表（meterType == 3）负充正放
        if (value > 0 && edge.target.node.meterType == 3) {
          isReverse = true
          break
        }
        if (value == 0 || value == null || value == '--') {
          isCancel = true
          break
        }
      }
    }

    // if (edge.target.node instanceof EBlockElement) {
    //   for (const key of keys) {
    //     const value = edge.target.node.getValue(key)
    //     if (value < 0) {
    //       isReverse = true
    //       break
    //     }
    //   }
    // }

    // if (edge.target.node instanceof ELinkElement) {
    //   for (const key of keys) {
    //     const value = edge.target.node.getValue(key)
    //     if (value < 0) {
    //       isReverse = true
    //       break
    //     }
    //   }
    // }


    points = isReverse ? [endX, endY, startX, startY] : points
    return { isReverse, isCancel, points }
  }

  _getLinePoints(source, target) {
    const { stage } = this.storage

    let sourcePortAbsPos = source.port.absolutePosition()
    let targetPortAbsPos = target.port.absolutePosition()

    let x1 = sourcePortAbsPos.x - stage.x()
    let y1 = sourcePortAbsPos.y - stage.y()
    let x2 = targetPortAbsPos.x - stage.x()
    let y2 = targetPortAbsPos.y - stage.y()

    return [x1, y1, x2, y2]
  }

  /**
   * 显示参考线
   * @param {*} target
   */
  showReferenceLine(target) {
    // 生成辅助线的步骤
    // 一：得到当前拖拽元素的left top值
    // 二：获得当前拖拽元素 和 参考元素(画布上非当前拖拽元素的其他元素)的相关信息,包括:width,height,left,top等
    // 三：遍历对比元素，通过x、y轴的分析生成辅助线数组
    // 四：根据当前拖拽元素的left top筛选出应该显示的辅助线

    // y轴辅助线有以下几种情况   （B为拖拽中的当前元素，A为静态参考元素，triggerTop为横辅助线触发条件，lineTop为横辅助线的y坐标）
    // 顶对顶 ： triggerTop = A.top, lineTop = A.top
    // 顶对底 ： triggerTop = A.top - B.height, lineTop = A.top
    // 中对中 ： triggerTop = A.top + A.height/2 - B.height/2, lineTop = A.top + A/height/2
    // 底对顶 ： triggerTop = A.top + A.height, lineTop = A.top + A.height
    // 底对底 ： triggerTop = A.tio + A.height - B.height, lineTop = A.top + A.height

    // x轴辅助线有以下几种情况 （B为拖拽中的当前元素，A为静态参考元素，triggerLeft为竖辅助线触发条件，lineLeft为竖辅助线的x坐）
    // 左对左 ： triggerLeft = A.left,  lineLeft = A.left
    // 左对右 ： triggerLeft = A.left + A.width,  lineLeft = A.left + A.width
    // 中对中 ： triggerLeft = A.left+ A.width/2 - B.width/2,  lineLeft = A.left + A.width/ 2
    // 右对右 ： triggerLeft: A.left + A.width - B.width,  lineLeft =  A.left + A.width
    // 右对左 ： triggerLeft =  A.left - B.width,  lineLeft =  A.left

    const { storage } = this
    const { stage } = storage
    // target.x()/y() 是图层局部坐标，而 triggerLeft/lineLeft 源自 getClientRect() 是绝对坐标(含画布平移)。
    // 统一到绝对坐标做判定，避免画布平移(stage.position)后参考线跑偏
    let x = target.x() + stage.x()
    let y = target.y() + stage.y()
    let positions = this._getComparePositions(target)
    let targetRect = target.getClientRect()
    let lines = this._calcLines(targetRect, positions)

    // 筛选辅助线
    const threshold = 5
    let yLine = null
    let xLine = null


    for (let i = 0; i < lines.y.length; i++) {
      const { triggerTop, lineTop } = lines.y[i]
      if (Math.abs(y - triggerTop) < threshold) {
        // 满足竖辅助线出现条件 为xLine赋值
        yLine = lineTop
        // 实现吸附效果
        // y = triggerTop

        // target.y(triggerTop)
        break
      }
    }

    for (let j = 0; j < lines.x.length; j++) {
      const { triggerLeft, lineLeft } = lines.x[j]
      if (Math.abs(x - triggerLeft) < threshold) {
        // 满足竖辅助线出现条件 为xLine赋值

        xLine = lineLeft
        // 实现吸附效果
        // x = triggerLeft;


        // target.x(triggerLeft)
        break
      }
    }
    // console.log({ yLine, xLine })

    if (yLine != null) {
      storage.yRefLine.show()
      // 绝对坐标 → 参考线图层(随舞台平移)的局部坐标
      storage.yRefLine.setY(yLine - stage.y())
    } else {
      storage.yRefLine.hide()
    }

    if (xLine != null) {
      storage.xRefLine.show()
      storage.xRefLine.setX(xLine - stage.x())
    } else {
      storage.xRefLine.hide()
    }
  }


  /**
   * 隐藏参考线
   */
  hideReferenceLine() {
    this.storage.xRefLine.hide()
    this.storage.yRefLine.hide()
  }

  /**
   * 计算参考线
   * @param {*} target 目标元素
   * @param {*} compares 待比较的元素
   * @returns
   */
  _calcLines(target, compares) {
    const x = []
    const y = []
    // 和其他所有参考物遍历
    for (const compare of compares) {
      DirectionX.forEach((item) => {
        // 构造竖辅助线数据
        x.push(item.calc(compare, target))
      })
      DirectionY.forEach((item) => {
        // 构造横辅助线数据
        y.push(item.calc(compare, target))
      })
    }
    return {
      x,
      y
    }
  }

  /**
  * 获取待比较元素的位置信息
  * @param {*} target
  * @returns
  */
  _getComparePositions(target) {
    let elems = this.storage.displayElement.filter(ge => {
      return ge.uuid !== target.uuid
    })

    return elems.map(elem => elem.keyShape.getClientRect())

  }
}
